import {
  Alert,
  AlertTitle,
  Button,
  styled as MuiStyled,
  Stack,
  Typography,
  buttonClasses,
  styled,
} from "@mui/material";

import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import logo from "../../img/google-logo.png";
import youtubelogo from "../../img/youtube-logo.png";
import { useNavigator } from "../../lib/Hooks";
import { Badge } from "../ui/badges";
import { BlankButton } from "../ui/buttons";

const SubHeader = styled("h3")(({ theme }) => ({
  fontSize: "15px",
  fontWeight: 700,
  borderBottom: `1px solid ${theme.palette.surfaceLight30.main}`,
  textTransform: "uppercase",
  marginTop: "30px",
  marginBottom: "20px",
}));

type BaseSaveErrorPropsType = {
  entity: string;
  errorMessage?: string | boolean;
  onCancel?: () => void;
  width?: string;
};

type BaseButtonProps = {
  id: string;
  testLabel?: string;
  isSubmitting?: boolean;
  isValid?: boolean;
};

type AddButtonProps = {
  id: string;
  testLabel?: string;
  url: string;
  label: string;
};

const BaseSaveError = ({ entity, errorMessage, onCancel, width = undefined }: BaseSaveErrorPropsType) => {
  if (errorMessage) {
    return (
      <Alert severity="error" onClose={onCancel} sx={{ width: width }}>
        <AlertTitle>Problem Saving {entity}</AlertTitle>
        <p>{errorMessage}</p>
      </Alert>
    );
  } else {
    return null;
  }
};

const AddButton = ({ id, url, label, testLabel = "add-button" }: AddButtonProps) => {
  const navigator = useNavigator();
  return (
    <Button
      id={id}
      data-cy={testLabel}
      size="small"
      onClick={() => {
        navigator.goto(url, { mode: "add" });
      }}
      startIcon={<PlusIcon />}
    >
      {label}
    </Button>
  );
};

type CancelButtonProps = {
  id: string;
  url: string;
  urlParams?: Record<string, string | number | undefined>;
  label: string;
};

const CancelButton = ({ id, url, urlParams, label }: CancelButtonProps) => {
  const navigator = useNavigator();
  return (
    <BlankButton
      id={id}
      onClick={() => {
        navigator.goto(url, urlParams);
      }}
    >
      {label}
    </BlankButton>
  );
};

// I will leave this colors as they are, as this component will be removed
const GoogleButton = MuiStyled(Button)({
  height: "30px",
  backgroundColor: "#4285F4",
  color: "white",
  textTransform: "none",
  borderRadius: "2px",
  [`&.${buttonClasses.root}`]: {
    padding: "3px 6px 3px 6px",
    ":hover": {
      backgroundColor: "#4285F4",
    },
  },
});

const YoutubeButton = MuiStyled(Button)(({ theme }) => ({
  height: "40px",
  width: "207px",
  backgroundColor: theme.palette.surfaceLight30.main,
  color: theme.palette.text10.main,
  variant: "text",
  textTransform: "none",
  [`&.${buttonClasses.root}`]: {
    "&:hover": {
      backgroundColor: theme.palette.surfaceLight30.main,
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: theme.palette.surfaceLight50.main,
    },
  },
}));

const GoogleLogo = styled("div")({
  backgroundColor: "white",
  width: "25px",
  height: "25px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const YoutubeLogo = styled("div")({
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

// Next time we want to work on this, we should consider using GoogleOauthButton.tsx and deprecate SubmitButtonGoogle
const SubmitButtonGoogle = ({ id, testLabel = "submit-button", isSubmitting, isValid }: BaseButtonProps) => {
  return (
    <Stack sx={{ flexDirection: "row", marginTop: "10px" }}>
      <GoogleButton
        id={id}
        type="submit"
        disabled={isSubmitting || !isValid}
        data-cy={testLabel}
        startIcon={
          <GoogleLogo>
            <img src={logo} alt="google-logo" width="20px" height="20px" />
          </GoogleLogo>
        }
      >
        <Typography
          sx={{ fontFamily: "Roboto", fontWeight: "bold", fontSize: "14px", marginTop: "3px", color: "text10.main" }}
        >
          Sign in with Google
        </Typography>
      </GoogleButton>
    </Stack>
  );
};

const SubmitButtonYoutube = ({ id, testLabel = "submit-button", isSubmitting, isValid }: BaseButtonProps) => {
  return (
    <Stack sx={{ flexDirection: "row" }}>
      <YoutubeButton
        id={id}
        type="submit"
        disabled={isSubmitting || !isValid}
        data-testid={testLabel}
        startIcon={
          <YoutubeLogo>
            <img src={youtubelogo} alt="youtube-logo" height="22px" />
          </YoutubeLogo>
        }
      >
        <Typography
          sx={{ fontFamily: "Roboto", fontWeight: "bold", fontSize: "24px", marginTop: "3px", color: "text10.main" }}
        ></Typography>
      </YoutubeButton>
    </Stack>
  );
};

const Suspended = ({ suspended }: { suspended?: boolean | null }) =>
  suspended ? <Badge size="small" data-testid="suspended-badge" label="Suspended" severity="warning" /> : null;

const FormComponent = {
  AddButton,
  BaseSaveError,
  CancelButton,
  SubHeader,
  Suspended,
  SubmitButtonGoogle,
  SubmitButtonYoutube,
};

export { FormComponent };
